import { motion } from 'motion/react';
import { cn } from '@/lib/utils/cn';
import { Card } from '@/components/ui/card';
import {
  FileEdit,
  ClipboardList,
  Ship,
  Landmark,
  CheckCircle2,
  ChevronRight,
  Clock,
  Timer,
  AlertTriangle,
  Zap,
  TrendingUp,
  Package,
  User,
  Plane,
  Truck,
  TrainFront,
  Container,
  ArrowRight,
  Route as RouteIcon,
} from 'lucide-react';
import type {
  ImportOrder,
  ImportOrderStatus,
  ImportLegStatus,
  TransportMode,
} from '../types/import-tracking.types';
import { LEG_STATUS_LABEL } from '../types/import-tracking.types';
import { getTransportModeConfig } from '../utils/transport-mode-labels';
import { TradeTypeBadge } from './trade-type-badge';

// ── Card helpers ─────────────────────────────────────────────────

const TRANSPORT_ICON: Record<string, typeof Ship> = {
  Ship,
  Plane,
  Truck,
  TrainFront,
  Container,
};

// Estado visual de cada estado de tramo
const LEG_STATUS_STYLE: Record<ImportLegStatus, string> = {
  PENDING: 'text-muted-foreground',
  IN_PROGRESS: 'text-alerta',
  COMPLETED: 'text-ok',
  DELAYED: 'text-bloqueo',
  CANCELLED: 'text-muted-foreground line-through',
};

type LegLite = {
  legOrder: number;
  status: ImportLegStatus;
  origin: string;
  destination: string;
  transportMode: TransportMode;
};

// El "tramo actual": el que está en curso/atrasado; si no, el próximo pendiente;
// si están todos cerrados, el último.
function getCurrentLeg(legs?: LegLite[]): LegLite | null {
  if (!legs || legs.length === 0) return null;
  return (
    legs.find((l) => l.status === 'IN_PROGRESS' || l.status === 'DELAYED') ??
    legs.find((l) => l.status === 'PENDING') ??
    legs[legs.length - 1]
  );
}

// ── Config ──────────────────────────────────────────────────────

interface PipelineColumn {
  status: ImportOrderStatus;
  label: string;
  icon: React.ElementType;
  gradient: string;
  headerGradient: string;
  borderColor: string;
  dotColor: string;
  accentHex: string;
  emoji: string;
}

const COLUMNS: PipelineColumn[] = [
  {
    status: 'QUOTE' as any,
    label: 'Cotización',
    icon: FileEdit,
    gradient: 'from-blue-400 to-blue-500',
    headerGradient: 'bg-info-suave',
    borderColor: 'border-t-blue-400',
    dotColor: 'bg-info',
    accentHex: '#60a5fa',
    emoji: '💰',
  },
  {
    status: 'DRAFT',
    label: 'Borrador',
    icon: FileEdit,
    gradient: 'from-slate-400 to-slate-500',
    headerGradient: 'bg-muted',
    borderColor: 'border-t-slate-400',
    dotColor: 'bg-muted-foreground',
    accentHex: '#94a3b8',
    emoji: '📝',
  },
  {
    status: 'CONFIRMED',
    label: 'Confirmada',
    icon: ClipboardList,
    gradient: 'from-blue-400 to-blue-600',
    headerGradient: 'bg-primary/10',
    borderColor: 'border-t-blue-500',
    dotColor: 'bg-info',
    accentHex: '#3b82f6',
    emoji: '✅',
  },
  {
    status: 'IN_TRANSIT',
    label: 'En tránsito',
    icon: Ship,
    gradient: 'from-amber-400 to-orange-500',
    headerGradient: 'bg-alerta-suave',
    borderColor: 'border-t-amber-500',
    dotColor: 'bg-alerta',
    accentHex: '#f59e0b',
    emoji: '🚢',
  },
  {
    status: 'CUSTOMS',
    label: 'En aduana',
    icon: Landmark,
    gradient: 'from-violet-400 to-purple-600',
    headerGradient: 'bg-ia-suave',
    borderColor: 'border-t-violet-500',
    dotColor: 'bg-violet-500',
    accentHex: '#8b5cf6',
    emoji: '🛃',
  },
  {
    status: 'DELIVERED',
    label: 'Entregada',
    icon: CheckCircle2,
    gradient: 'from-emerald-400 to-green-600',
    headerGradient: 'bg-ok-suave',
    borderColor: 'border-t-emerald-500',
    dotColor: 'bg-ok',
    accentHex: '#10b981',
    emoji: '🎉',
  },
];

/**
 * A qué columna va cada estado.
 *
 * 🔴 Antes `CUSTOMS` caía en "En Tránsito" junto con `SHIPPED` y `CUSTOMS_HOLD`,
 * bajo el rótulo de "estados legacy". Una orden que estaba en aduana aparecía
 * como si siguiera navegando: el tablero mentía sobre dónde estaba la carga, que
 * es exactamente lo único que un kanban tiene que decir.
 *
 * Pasó de latente a real el 2026-08-13, cuando completar el último tramo
 * internacional empezó a mover la orden a `CUSTOMS` sola. Antes casi nadie la
 * ponía a mano y por eso no se notaba.
 *
 * Los dos que siguen sin columna propia son matices, no etapas:
 *
 *   SHIPPED       → zarpó, o sea que está en tránsito
 *   CUSTOMS_HOLD  → retenida EN aduana: es la misma etapa, con un problema
 *
 * Los dos van a la columna de su etapa y se distinguen en la tarjeta, que es
 * donde el matiz importa. Inventarles columna dejaría un tablero de ocho
 * columnas donde seis están casi siempre vacías.
 */
function getColumnStatus(status: ImportOrderStatus): ImportOrderStatus {
  if (status === 'SHIPPED') return 'IN_TRANSIT';
  if (status === 'CUSTOMS_HOLD') return 'CUSTOMS';
  return status;
}

// ── Helpers ─────────────────────────────────────────────────────

function formatMoney(value: number | null | undefined, currency = 'USD'): string {
  if (value == null || isNaN(value)) return `${currency} 0`;
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function daysAgo(dateStr: string): string {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'Hoy';
  if (days === 1) return 'Ayer';
  return `${days}d`;
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-BO', { day: '2-digit', month: 'short' });
}

function daysUntilETA(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return Math.round((new Date(dateStr).getTime() - Date.now()) / 86400000);
}

function getETAUrgency(days: number | null): {
  color: string;
  bg: string;
  icon: React.ElementType;
  label: string;
} {
  if (days === null)
    return { color: 'text-muted-foreground', bg: 'bg-muted/40', icon: Clock, label: '' };
  if (days < 0) return { color: 'text-ok', bg: 'bg-ok-suave', icon: CheckCircle2, label: 'Llegó' };
  if (days <= 2)
    return { color: 'text-bloqueo', bg: 'bg-bloqueo-suave', icon: Zap, label: 'Urgente' };
  if (days <= 7)
    return { color: 'text-alerta', bg: 'bg-alerta-suave', icon: AlertTriangle, label: 'Pronto' };
  if (days <= 15)
    return { color: 'text-primary', bg: 'bg-info-suave', icon: Timer, label: 'En camino' };
  return { color: 'text-muted-foreground', bg: 'bg-muted', icon: Clock, label: 'Planificado' };
}

/**
 * En qué punto del recorrido está cada estado, para la barra de la tarjeta.
 *
 * 🔴 Estaba peleado con el tablero: el kanban dibuja "Cotización" como PRIMERA
 * columna y acá valía `-1`, o sea fuera del recorrido. Con la cuenta de abajo
 * —`idx / 5`— una cotización mostraba **−20 % de avance**. Dos componentes de la
 * misma pantalla no pueden estar en desacuerdo sobre si cotizar es una etapa.
 *
 * Ahora los números son las MISMAS seis etapas que las columnas, en el mismo
 * orden. `SHIPPED` y `CUSTOMS_HOLD` comparten el punto de su etapa, como
 * comparten columna.
 */
const ETAPAS_DEL_RECORRIDO = 5;

const STATUS_PIPELINE_INDEX: Record<ImportOrderStatus, number> = {
  QUOTE: 0,
  DRAFT: 1,
  CONFIRMED: 2,
  SHIPPED: 3,
  IN_TRANSIT: 3,
  CUSTOMS: 4,
  CUSTOMS_HOLD: 4,
  DELIVERED: 5,
  // Cancelada no está en el recorrido: se muestra aparte, fuera del tablero.
  CANCELLED: 0,
};

// ── Component ───────────────────────────────────────────────────

interface ImportPipelineBoardProps {
  orders: ImportOrder[];
  onOrderClick: (orderId: string) => void;
  className?: string;
}

export function ImportPipelineBoard({ orders, onOrderClick, className }: ImportPipelineBoardProps) {
  const grouped = new Map<ImportOrderStatus, ImportOrder[]>();
  for (const col of COLUMNS) grouped.set(col.status, []);
  for (const order of orders) {
    if (order.status === 'CANCELLED') continue;
    const list = grouped.get(getColumnStatus(order.status));
    if (list) list.push(order);
  }

  const cancelledOrders = orders.filter((o) => o.status === 'CANCELLED');
  return (
    <div className={cn('space-y-4', className)}>
      {/* Pipeline board */}
      <div className="flex gap-2 overflow-x-auto pb-4 min-h-[420px]">
        {COLUMNS.map((col, colIdx) => {
          const colOrders = grouped.get(col.status) || [];
          const Icon = col.icon;
          const hasOrders = colOrders.length > 0;

          return (
            <motion.div
              key={col.status}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: colIdx * 0.07, duration: 0.5, ease: 'easeOut' }}
              className="flex items-start gap-0"
            >
              <div
                className={cn(
                  'w-[210px] flex-shrink-0 rounded-2xl border-t-4 bg-card border border-border/60 shadow-sm',
                  col.borderColor,
                  hasOrders && 'shadow-md'
                )}
              >
                {/* Header */}
                <div className={cn('p-3 rounded-t-xl', col.headerGradient)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{col.emoji}</span>
                      <span className="text-sm font-bold" style={{ color: col.accentHex }}>
                        {col.label}
                      </span>
                    </div>
                    <div
                      className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-black text-white shadow-sm"
                      style={{ backgroundColor: col.accentHex }}
                    >
                      {colOrders.length}
                    </div>
                  </div>
                  {/* Aggregate FOB for column */}
                  {colOrders.length > 0 && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <TrendingUp className="w-3 h-3" style={{ color: col.accentHex }} />
                      <span
                        className="text-[10px] font-semibold tabular-nums"
                        style={{ color: col.accentHex }}
                      >
                        {formatMoney(colOrders.reduce((s, o) => s + (o.totalFobValue ?? 0), 0))}
                      </span>
                    </div>
                  )}
                </div>

                {/* Cards */}
                <div className="p-2 space-y-2 min-h-[280px]">
                  {colOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                      <Icon className="w-8 h-8 opacity-10 mb-1" />
                      <span className="text-xs">Sin órdenes</span>
                    </div>
                  ) : (
                    colOrders.map((order, cardIdx) => (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: colIdx * 0.07 + cardIdx * 0.05, duration: 0.35 }}
                      >
                        <PipelineCard
                          order={order}
                          column={col}
                          onClick={() => onOrderClick(order.id)}
                        />
                      </motion.div>
                    ))
                  )}
                </div>
              </div>

              {/* Arrow connector */}
              {colIdx < COLUMNS.length - 1 && (
                <div className="flex items-center h-14 mt-3 px-0.5 flex-shrink-0">
                  <motion.div
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <ChevronRight className="w-4 h-4 text-muted-foreground/30" />
                  </motion.div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Cancelled orders */}
      {cancelledOrders.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-2"
        >
          <p className="text-xs text-muted-foreground mb-2 font-medium">
            Canceladas ({cancelledOrders.length})
          </p>
          <div className="flex gap-2 flex-wrap">
            {cancelledOrders.map((order) => (
              <button
                key={order.id}
                onClick={() => onOrderClick(order.id)}
                className="px-3 py-1.5 rounded-lg bg-bloqueo-suave text-bloqueo text-xs font-mono font-semibold border border-bloqueo-borde hover:border-bloqueo-borde hover:shadow-sm transition-all"
              >
                {order.orderNumber}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ── Pipeline Card ───────────────────────────────────────────────

function PipelineCard({
  order,
  column,
  onClick,
}: {
  order: ImportOrder;
  column: PipelineColumn;
  onClick: () => void;
}) {
  const isInTransit = order.status === 'IN_TRANSIT';
  const isCustomsHold = order.status === 'CUSTOMS_HOLD';
  const isDelivered = order.status === 'DELIVERED';

  const etaDays = daysUntilETA(order.estimatedArrivalDate);
  const etaUrgency = getETAUrgency(etaDays);

  // Progress bar percentage
  const pipelineIdx = STATUS_PIPELINE_INDEX[order.status] ?? 0;
  // Acotado: un estado que mañana no esté en la tabla no puede dibujar una barra
  // negativa ni pasada de largo.
  const progressPct = Math.min(
    100,
    Math.max(0, Math.round((pipelineIdx / ETAPAS_DEL_RECORRIDO) * 100))
  );

  // Tramo actual + estado (legs vienen livianos desde el listado)
  const legs = order.legs as LegLite[] | undefined;
  const currentLeg = getCurrentLeg(legs);

  // Ruta: usar puertos de la orden o, si faltan, los extremos de los tramos
  const routeOrigin = order.originPort || legs?.[0]?.origin || '';
  const routeDest = order.destinationPort || legs?.[legs.length - 1]?.destination || '';
  const hasRoute = !!(routeOrigin || routeDest);

  const TransportIcon = TRANSPORT_ICON[getTransportModeConfig(order.transportMode).icon] ?? Ship;

  return (
    <Card
      className={cn(
        'cursor-pointer overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1 group border-0',
        isInTransit && 'ring-2 ring-alerta/50',
        isCustomsHold && 'ring-2 ring-bloqueo/60'
      )}
      onClick={onClick}
    >
      {/* Top color stripe */}
      <div className={cn('h-1 bg-gradient-to-r', column.gradient)} />

      <div className="p-3 space-y-2.5">
        {/* Order number + status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-mono text-sm font-black text-foreground group-hover:text-primary transition-colors">
              {order.orderNumber}
            </span>
            <TradeTypeBadge
              tradeType={order.tradeType}
              className="px-1 py-0 text-[9px] font-semibold shrink-0"
            />
          </div>
          {isCustomsHold && (
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-bloqueo-suave text-bloqueo text-[9px] font-bold animate-pulse">
              ⚠️ Retenida
            </span>
          )}
          {isDelivered && <span className="text-sm">🎉</span>}
        </div>

        {/* Cliente */}
        <div className="flex items-center gap-1.5">
          <User className="w-3 h-3 shrink-0 text-muted-foreground/60" />
          <span className="text-xs font-medium text-foreground truncate">
            {order.customer?.name ?? '—'}
          </span>
        </div>

        {/* Quién la creó (el cotizador). La gerente ve todas y así sabe de quién es. */}
        {order.createdBy && (
          <div className="text-[11px] text-muted-foreground truncate" title="Creada por">
            por {order.createdBy.firstName} {order.createdBy.lastName}
          </div>
        )}

        {/* Ruta + incoterm */}
        {(hasRoute || order.incoterm) && (
          <div className="flex items-center justify-between gap-1.5">
            {hasRoute ? (
              <div className="flex items-center gap-1 min-w-0 text-[11px] text-muted-foreground">
                <TransportIcon className="w-3 h-3 shrink-0" />
                <span className="truncate">{routeOrigin || '—'}</span>
                <ArrowRight className="w-2.5 h-2.5 shrink-0" />
                <span className="truncate">{routeDest || '—'}</span>
              </div>
            ) : (
              <span />
            )}
            {order.incoterm && (
              <span className="shrink-0 px-1.5 py-0.5 rounded border border-border bg-muted/50 text-[9px] font-bold text-muted-foreground">
                {order.incoterm}
              </span>
            )}
          </div>
        )}

        {/* Proveedor de flete */}
        {order.freightProvider?.name && (
          <div className="flex items-center gap-1.5">
            <Package className="w-3 h-3 shrink-0 text-muted-foreground/50" />
            <span className="text-[11px] text-muted-foreground truncate">
              {order.freightProvider.name}
            </span>
          </div>
        )}

        {/* Progress bar */}
        <div className="space-y-1">
          <div className="w-full h-1.5 bg-muted/60 rounded-full overflow-hidden">
            <motion.div
              className={cn('h-full rounded-full bg-gradient-to-r', column.gradient)}
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
            />
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="font-bold tabular-nums" style={{ color: column.accentHex }}>
              {progressPct}%
            </span>
            <span className="text-muted-foreground flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" />
              {daysAgo(order.updatedAt)}
            </span>
          </div>
        </div>

        {/* FOB */}
        <div className="text-xs font-bold tabular-nums text-foreground">
          {formatMoney(order.totalFobValue, order.currency)}
          {order.totalLandedCost > 0 && (
            <span className="text-primary ml-1.5">
              → {formatMoney(order.totalLandedCost, order.currency)}
            </span>
          )}
        </div>

        {/* ETA badge */}
        {order.estimatedArrivalDate && !isDelivered && (
          <div
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold',
              etaUrgency.bg
            )}
          >
            <etaUrgency.icon className={cn('w-3 h-3', etaUrgency.color)} />
            <span className={etaUrgency.color}>
              {etaDays !== null && etaDays >= 0 ? (
                <>
                  {etaDays === 0
                    ? 'Llega HOY'
                    : etaDays === 1
                      ? 'Llega MAÑANA'
                      : `${etaDays} días → `}
                  {etaDays > 1 && formatShortDate(order.estimatedArrivalDate)}
                </>
              ) : etaDays !== null && etaDays < 0 ? (
                `${Math.abs(etaDays)}d retraso`
              ) : (
                formatShortDate(order.estimatedArrivalDate)
              )}
            </span>
          </div>
        )}

        {/* Estado del tramo actual */}
        {currentLeg && legs && (
          <div className="flex items-center gap-1.5 text-[10px]">
            <RouteIcon className="w-3 h-3 shrink-0 text-muted-foreground/60" />
            <span className="text-muted-foreground">
              Tramo {currentLeg.legOrder}/{legs.length} ·{' '}
            </span>
            <span className={cn('font-semibold', LEG_STATUS_STYLE[currentLeg.status])}>
              {LEG_STATUS_LABEL[currentLeg.status]}
            </span>
          </div>
        )}

        {/* Items */}
        <div className="text-[10px] text-muted-foreground">
          {order._count.items} producto{order._count.items !== 1 ? 's' : ''}
        </div>
      </div>
    </Card>
  );
}
